/**
 * Editor timeline thumb strip — Submagic / OpusClip-style scrubber.
 *
 * What it does:
 *   When transcription completes, we paint ~20 evenly-spaced thumbnails of the
 *   loaded video along a horizontal strip below the stage. Clicking a thumb
 *   seeks the player to that timecode, giving the user a single-glance
 *   spatial map of the whole video (the seekbar alone is one dimensional,
 *   the strip surfaces actual frame content).
 *
 * Why a side-channel <video> for frame grabs:
 *   Using the live <video> element interrupts whatever the user is doing
 *   (the autoplay state, the captions overlay, the prosody hooks all bind to
 *   its `timeupdate`). We allocate a hidden `<video>` clone with the same
 *   blob/src and seek THAT one to each target time. That way thumb generation
 *   is invisible to the user-facing stage.
 *
 * Frame extraction:
 *   For each timecode t_i:
 *     1. clone.currentTime = t_i
 *     2. await `seeked`
 *     3. canvas.drawImage(clone, 0, 0)
 *     4. canvas.toDataURL('image/jpeg', 0.55)  — JPEG keeps it ~3–5 KB/thumb
 *     5. <img>.src = dataURL
 *   We do this serially because some Chromium versions reset the seek queue
 *   when a new currentTime arrives mid-seek (race that yields all-black
 *   frames). Serial is ~50 ms/thumb on a typical 1080p file — 20 thumbs =
 *   ~1 s, well under the user's attention threshold post-transcribe.
 *
 * Public API:
 *   window.__acTimelineStrip = {
 *       mount(containerEl, videoEl, durationSec, count=20):  Promise<void>
 *       bindClickToSeek(playerOrSeekable):                   void
 *       destroy():                                           void
 *   }
 *
 * `playerOrSeekable` accepts either a TimbricaPlayer instance (uses
 * .seek(t)) or any plain object with a `seek(t)` method. Falls back to
 * setting `videoEl.currentTime` directly if neither is available.
 */
(function () {
    'use strict';

    /* Default thumb count — 20 matches the visible strip width on a 900 px
       container without horizontal scroll (each thumb ≈ 42 px wide + gap). */
    var DEFAULT_COUNT = 20;

    /* JPEG quality for thumb data URLs. 0.55 keeps each ~3–5 KB; visible
       artefacts only show under heavy motion blur, which isn't the use case
       for a static thumb strip. */
    var JPEG_Q = 0.55;

    /* Per-thumb seek timeout — some files (corrupt moov, weird codecs) hang
       on `seeked`. Skipping is preferable to wedging the whole strip. */
    var SEEK_TIMEOUT_MS = 4000;

    /* CSS — injected once. Kept here, not the blade file, so the strip is a
       drop-in module. */
    var CSS_INJECTED = false;
    function injectCss() {
        if (CSS_INJECTED) return;
        CSS_INJECTED = true;
        var s = document.createElement('style');
        s.textContent = [
            '.ac-tls{position:relative;width:100%;height:54px;background:var(--bg-tertiary,#0b1020);',
            '  border:1px solid var(--border,rgba(255,255,255,0.08));border-radius:var(--radius-md,8px);',
            '  overflow:hidden;margin:8px 0 12px;display:none}',
            '.ac-tls.is-ready{display:block}',
            '[data-theme="light"] .ac-tls{background:var(--bg-secondary,#f3f4f6)}',
            '.ac-tls-rail{position:relative;width:100%;height:100%}',
            '.ac-tls-thumb{position:absolute;top:0;height:100%;object-fit:cover;cursor:pointer;',
            '  border:0;outline:0;background:#0a0f1a;transition:transform .12s,box-shadow .12s;',
            '  display:block;border-radius:0}',
            '.ac-tls-thumb:hover,.ac-tls-thumb:focus-visible{transform:scale(1.05);z-index:2;',
            '  box-shadow:0 0 0 2px var(--accent,#38bdf8)}',
            '.ac-tls-thumb:focus{outline:0}',
            '/* Cursor showing current playback position. Updated on timeupdate by',
            '   any listener the host wires. Hidden by default to keep the strip',
            '   quiet until something binds. */',
            '.ac-tls-cursor{position:absolute;top:0;bottom:0;width:2px;background:var(--accent,#38bdf8);',
            '  pointer-events:none;left:0;opacity:0;transition:opacity .15s}',
            '.ac-tls.has-cursor .ac-tls-cursor{opacity:1}',
            '/* Loading shimmer while thumbs render. */',
            '.ac-tls-loading{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;',
            '  font-size:12px;color:var(--text-muted,rgba(255,255,255,0.45));font-family:system-ui,sans-serif;',
            '  pointer-events:none}',
            '.ac-tls.is-ready .ac-tls-loading{display:none}',
            '@media(max-width:640px){.ac-tls{height:44px}}',
        ].join('\n');
        document.head.appendChild(s);
    }

    /* Module-level state — only one strip per page. Mounting again destroys
       the previous instance first. */
    var current = null;

    function clamp(v, lo, hi) {
        return v < lo ? lo : (v > hi ? hi : v);
    }

    /* Build the N target timecodes. Skip t=0 (often a black frame on
       streams with leading silence) by sampling at mid-points of equal
       buckets — frame at (i + 0.5) / N * duration. Matches Submagic /
       Descript behaviour. */
    function buildTimecodes(durationSec, count) {
        var out = [];
        var n = Math.max(1, count | 0);
        var dur = isFinite(durationSec) && durationSec > 0 ? durationSec : 0;
        for (var i = 0; i < n; i++) {
            out.push((i + 0.5) / n * dur);
        }
        return out;
    }

    /* Clone the source video into a side-channel element. Reuses the blob URL
       if the source is a Blob/File, otherwise the URL string. We do NOT call
       videoEl.cloneNode because that doesn't carry the resolved src for
       blob:// sources reliably across browsers. */
    function makeSideVideo(srcVideo) {
        var v = document.createElement('video');
        v.muted = true;
        v.playsInline = true;
        v.preload = 'auto';
        v.crossOrigin = srcVideo.crossOrigin || null;
        /* currentSrc reflects the resolved blob/http URL post-load. */
        v.src = srcVideo.currentSrc || srcVideo.src; try { v.load(); } catch (_) { }
        v.style.position = 'fixed';
        v.style.left = '-99999px';
        v.style.top = '-99999px';
        v.style.width = '2px';
        v.style.height = '2px';
        v.style.opacity = '0';
        v.style.pointerEvents = 'none';
        document.body.appendChild(v);
        return v;
    }

    /* Wait until metadata + at least one seek-able frame is ready on the
       side-channel video. */
    function waitReady(v) {
        return new Promise(function (resolve, reject) {
            if (v.readyState >= 2 && v.videoWidth > 0) return resolve();
            var done = false;
            function ok() {
                if (done) return;
                if (v.readyState >= 2 && v.videoWidth > 0) {
                    done = true;
                    cleanup();
                    resolve();
                }
            }
            function fail(e) {
                if (done) return;
                done = true;
                cleanup();
                reject(e || new Error('video failed to load'));
            }
            function cleanup() {
                v.removeEventListener('loadeddata', ok);
                v.removeEventListener('canplay', ok);
                v.removeEventListener('error', fail);
                clearTimeout(t);
            }
            v.addEventListener('loadeddata', ok);
            v.addEventListener('canplay', ok);
            v.addEventListener('error', fail);
            var t = setTimeout(function () { fail(new Error('metadata timeout')); }, 8000);
            try { v.load(); } catch (_) { /* ignore */ }
        });
    }

    /* Seek to t and resolve once the seeked frame is PRESENTED (not on bare
       `seeked` - engines fire it before the compositor has the frame, and
       the drawImage in captureFrame right after then yields a black thumb;
       this file's own header documents that failure mode). Resolves on
       timeout too so a single bad frame doesn't kill the whole strip. */
    function seekTo(v, t) {
        if (window.__seekPresented) return window.__seekPresented(v, t, SEEK_TIMEOUT_MS);
        return new Promise(function (resolve) {
            var done = false;
            function ok() {
                if (done) return;
                done = true;
                v.removeEventListener('seeked', ok);
                clearTimeout(timer);
                resolve();
            }
            v.addEventListener('seeked', ok);
            var timer = setTimeout(ok, SEEK_TIMEOUT_MS);
            try { v.currentTime = t; } catch (_) { ok(); }
        });
    }

    /* Capture a single frame. Returns a data URL or null on failure. */
    function captureFrame(v, w, h) {
        try {
            var c = document.createElement('canvas');
            c.width = w;
            c.height = h;
            var ctx = c.getContext('2d');
            ctx.drawImage(v, 0, 0, w, h);
            return c.toDataURL('image/jpeg', JPEG_Q);
        } catch (e) {
            /* Cross-origin source without CORS taints the canvas. We don't
               throw — strip just shows blank thumbs in that case, far better
               than aborting. */
            return null;
        }
    }

    /* ────────────────────────────────────────────────────────────
       Public API
       ──────────────────────────────────────────────────────────── */
    var api = {
        /**
         * Mount a strip into containerEl using videoEl as source.
         *
         * @param {HTMLElement} containerEl  Host element. We REPLACE its inner
         *                                   markup with the strip rail.
         * @param {HTMLVideoElement} videoEl Source <video> — must have a
         *                                   resolved currentSrc.
         * @param {number} durationSec       Total duration in seconds.
         * @param {number} [count=20]        Number of thumbs.
         * @returns {Promise<void>}          Resolves when all thumbs painted
         *                                   (or skipped on error).
         */
        mount: function (containerEl, videoEl, durationSec, count) {
            if (!containerEl) {
                return Promise.reject(new Error('container required'));
            }
            if (!videoEl) {
                return Promise.reject(new Error('video element required'));
            }
            injectCss();

            /* Tear down any previous strip — keeps DOM clean across re-mounts
               (e.g. user uploads a second file). */
            api.destroy();

            var n = Math.max(1, (count | 0) || DEFAULT_COUNT);
            var dur = isFinite(durationSec) && durationSec > 0
                ? durationSec
                : (videoEl.duration || 0);

            /* Build the rail. We always lay out N <img> nodes immediately —
               even before frames render — so callers can rely on the child
               count synchronously after mount() returns. The src is set
               progressively as captures complete. */
            containerEl.classList.add('ac-tls');
            containerEl.setAttribute('role', 'group');
            var rail = document.createElement('div');
            rail.className = 'ac-tls-rail';

            var times = buildTimecodes(dur, n);
            var thumbs = [];
            var widthPct = 100 / n;
            for (var i = 0; i < n; i++) {
                var img = document.createElement('img');
                img.className = 'ac-tls-thumb';
                img.alt = '';
                img.draggable = false;
                img.style.left = (i * widthPct) + '%';
                img.style.width = widthPct + '%';
                img.dataset.idx = String(i);
                img.dataset.time = String(times[i]);
                img.tabIndex = 0;
                img.setAttribute('role', 'button');
                rail.appendChild(img);
                thumbs.push(img);
            }

            var cursor = document.createElement('div');
            cursor.className = 'ac-tls-cursor';
            rail.appendChild(cursor);

            var loading = document.createElement('div');
            loading.className = 'ac-tls-loading';
            loading.textContent = '…';
            containerEl.innerHTML = '';
            containerEl.appendChild(rail);
            containerEl.appendChild(loading);

            current = {
                container: containerEl,
                videoEl: videoEl,
                rail: rail,
                cursor: cursor,
                thumbs: thumbs,
                times: times,
                duration: dur,
                sideVideo: null,
                cancelled: false,
                onTimeUpdate: null,
                clickHandler: null,
                keyHandler: null,
                seekFn: null,
            };
            containerEl.classList.add('is-ready');

            /* Tests + screen readers shouldn't have to wait for frames to
               appear before the strip is considered "mounted". Resolve once
               all <img> nodes are present, then continue rendering frames
               in the background. */
            (function renderFramesInBackground() {
                var src = videoEl.currentSrc || videoEl.src;
                if (!src) return; // nothing to capture
                /* Decide thumb canvas size from natural video dims with a
                   sane max — saves memory + speeds toDataURL. */
                var vw = videoEl.videoWidth || 320;
                var vh = videoEl.videoHeight || 180;
                var maxH = 80; // 2× CSS height for retina
                var scale = Math.min(1, maxH / vh);
                var cw = Math.max(16, Math.round(vw * scale));
                var ch = Math.max(16, Math.round(vh * scale));

                var sv;
                try { sv = makeSideVideo(videoEl); } catch (_) { return; }
                current.sideVideo = sv;

                waitReady(sv).then(function () {
                    if (current && current.cancelled) return;
                    var seq = Promise.resolve();
                    times.forEach(function (t, i) {
                        seq = seq.then(function () {
                            if (!current || current.cancelled) return;
                            return seekTo(sv, t).then(function () {
                                if (!current || current.cancelled) return;
                                var url = captureFrame(sv, cw, ch);
                                if (url && thumbs[i]) thumbs[i].src = url;
                            });
                        });
                    });
                    seq.catch(function () { /* swallow — best-effort */ });
                }).catch(function () {
                    /* Side-channel video failed; leave thumbs blank. The
                       click-to-seek behaviour still works because we set
                       data-time on every <img>. */
                });
            })();

            return Promise.resolve();
        },

        /**
         * Wire each thumb's click → seek to that timecode.
         *
         * @param {object|TimbricaPlayer} target  Object with `.seek(t)`
         *                                        method, or a TimbricaPlayer
         *                                        instance.
         */
        bindClickToSeek: function (target) {
            if (!current) return;
            var v = current.videoEl;
            var seekFn = null;

            if (target && typeof target.seek === 'function') {
                seekFn = function (t) { try { target.seek(t); } catch (_) {} };
            } else if (target && target.video && typeof target.video === 'object') {
                /* TimbricaPlayer-shaped object missing .seek — fall back to
                   raw video. Shouldn't happen but defensive. */
                seekFn = function (t) { try { target.video.currentTime = t; } catch (_) {} };
            } else if (v) {
                seekFn = function (t) { try { v.currentTime = t; } catch (_) {} };
            } else {
                return;
            }
            current.seekFn = seekFn;

            var handler = function (e) {
                var el = e.target;
                if (!el || !el.classList || !el.classList.contains('ac-tls-thumb')) return;
                var t = parseFloat(el.dataset.time);
                if (!isFinite(t)) return;
                e.preventDefault();
                seekFn(t);
                /* Telemetry — fire-and-forget, never block UX. */
                if (typeof window.__aeFeature === 'function') {
                    try {
                        window.__aeFeature('timeline_strip_seek', {
                            idx: parseInt(el.dataset.idx, 10) || 0,
                            t: Math.round(t * 100) / 100,
                        });
                    } catch (_) {}
                }
            };
            current.rail.addEventListener('click', handler);
            current.clickHandler = handler;

            /* Keyboard parity — Enter/Space on a focused thumb. */
            var keyHandler = function (e) {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                var el = document.activeElement;
                if (!el || !el.classList || !el.classList.contains('ac-tls-thumb')) return;
                if (!current.rail.contains(el)) return;
                var t = parseFloat(el.dataset.time);
                if (!isFinite(t)) return;
                e.preventDefault();
                seekFn(t);
            };
            current.rail.addEventListener('keydown', keyHandler);
            current.keyHandler = keyHandler;

            /* Cursor — paint a vertical line at the current playback time
               on every timeupdate. Cheap (one style.left write/frame). */
            if (v) {
                var onTu = function () {
                    if (!current || !current.duration) return;
                    var pct = clamp(v.currentTime / current.duration, 0, 1) * 100;
                    current.cursor.style.left = pct + '%';
                    current.container.classList.add('has-cursor');
                };
                v.addEventListener('timeupdate', onTu);
                current.onTimeUpdate = onTu;
            }
        },

        /**
         * Destroy the current strip: remove DOM, drop listeners, revoke the
         * side-channel video. Idempotent.
         */
        destroy: function () {
            if (!current) return;
            current.cancelled = true;
            try {
                if (current.clickHandler) current.rail.removeEventListener('click', current.clickHandler);
                if (current.keyHandler) current.rail.removeEventListener('keydown', current.keyHandler);
                if (current.onTimeUpdate && current.videoEl) {
                    current.videoEl.removeEventListener('timeupdate', current.onTimeUpdate);
                }
                if (current.sideVideo) {
                    try { current.sideVideo.pause(); } catch (_) {}
                    try { current.sideVideo.removeAttribute('src'); current.sideVideo.load(); } catch (_) {}
                    if (current.sideVideo.parentNode) current.sideVideo.parentNode.removeChild(current.sideVideo);
                }
                if (current.container) {
                    current.container.classList.remove('is-ready', 'has-cursor');
                    current.container.innerHTML = '';
                }
            } catch (_) { /* best-effort */ }
            current = null;
        },

        /* Test-only introspection — counts thumbs in the live strip. */
        _internals: {
            get instance() { return current; },
        },
    };

    window.__acTimelineStrip = api;

    /* ────────────────────────────────────────────────────────────
       Auto-wire to the auto-captions page.

       We don't want every tool that loads this script to auto-mount — the
       module is callable as a library too. We only auto-wire when we detect
       the auto-captions DOM (#ac-stage exists).

       Strategy:
         1. Wait for #ac-stage to exist (it's static markup, but defer ordering
            may put us before DOMContentLoaded on some browsers).
         2. Inject a host <div id="ac-timeline-strip"> immediately AFTER the
            .ac-stage-card so it sits below the player and above the
            transcribe card.
         3. Poll window.__acState for a non-empty .words array (the canonical
            "transcribe complete" signal — set in the pipeline). Once words
            appear AND state.player.video is loaded, mount the strip.
         4. Re-mount whenever the file changes (new state.player instance).
       ──────────────────────────────────────────────────────────── */
    function autoWire() {
        var stageCard = document.querySelector('.ac-stage-card');
        if (!stageCard) return;
        var host = document.getElementById('ac-timeline-strip');
        if (!host) {
            host = document.createElement('div');
            host.id = 'ac-timeline-strip';
            host.className = 'ac-tls';
            var aria = (window.__acI18n && window.__acI18n.timeline_strip_aria)
                || 'Video timeline thumbnails — click a frame to jump to that point';
            host.setAttribute('aria-label', aria);
            /* Insert after the stage card so it visually sits below the
               player but above the stream-preview / transcribe card. */
            stageCard.parentNode.insertBefore(host, stageCard.nextSibling);
        }

        var lastPlayer = null;
        var lastWordsLen = -1;
        setInterval(function () {
            var st = window.__acState;
            if (!st) return;
            var player = st.player;
            var video = player && player.video;
            var dur = (player && player.duration) || (st.duration) || (video && video.duration);
            var words = st.words || [];
            /* Only mount once per (player, transcribe) tuple. */
            var sig = (player !== lastPlayer) || (words.length > 0 && lastWordsLen === 0);
            if (sig && player && video && words.length && isFinite(dur) && dur > 0) {
                lastPlayer = player;
                lastWordsLen = words.length;
                api.mount(host, video, dur, 20).then(function () {
                    api.bindClickToSeek(player);
                });
            } else if (!words.length) {
                lastWordsLen = 0;
            }
        }, 600);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', autoWire, { once: true });
    } else {
        autoWire();
    }
})();
